import { useState, useEffect } from "react";

/**
 * Custom hook to manage dropdown filters and filtered table data.
 * @param {Array} dropdownFields - List of fields to create dropdown filters for.
 * @param {Array} dropdownTable - Table data to filter based on dropdown selections.
 * @returns {Object} - Contains dropdown options, selected values, filtered table data, and a handler for dropdown changes.
 */
export function useDropdownFilters(dropdownFields, dropdownTable) {
    // Initialize selected values for each dropdown field with "All" (indicating no filter applied).
    const initialSelectedValues = dropdownFields.reduce((acc, field) => {
        acc[field] = "All";
        return acc;
    }, {});

    // State to store dropdown options for each field.
    const [dropdownOptions, setDropdownOptions] = useState(
        dropdownFields.reduce((acc, field) => ({ ...acc, [field]: [] }), {})
    );

    // State to store the currently selected values for each dropdown field.
    const [selectedValues, setSelectedValues] = useState(initialSelectedValues);

    /**
     * Effect to dynamically update dropdown options based on the current table data and selected values.
     * Ensures that dropdown options are filtered and unique for each field.
     */
    useEffect(() => {
        if (dropdownTable.length > 0) {
            const newDropdownOptions = dropdownFields.reduce((options, field) => {
                // Filter the table data based on the selected values of other fields.
                const filteredData = dropdownTable.filter(item =>
                    dropdownFields.every(otherField =>
                        otherField === field || 
                        selectedValues[otherField] === "All" || 
                        item[otherField] === selectedValues[otherField]
                    )
                );

                // Extract unique values for the current field and sort them.
                options[field] = [...new Set(filteredData.map(item => item[field]))].sort();
                return options;
            }, {});

            // Update dropdown options only if they have changed to avoid unnecessary re-renders.
            setDropdownOptions(prevOptions => {
                const hasChanged = dropdownFields.some(field => {
                    const prevFieldOptions = prevOptions[field] || [];
                    const newFieldOptions = newDropdownOptions[field] || [];
                    return prevFieldOptions.length !== newFieldOptions.length ||
                        prevFieldOptions.some((value, index) => value !== newFieldOptions[index]);
                });

                return hasChanged ? newDropdownOptions : prevOptions;
            });
        }
    }, [dropdownTable, selectedValues, dropdownFields]);

    /**
     * Handler to update the selected value for a specific dropdown field.
     * @param {string} field - The field name of the dropdown.
     * @param {string} value - The selected value for the dropdown.
     */
    const handleDropdownChange = (field, value) => {
        setSelectedValues(prev => ({ ...prev, [field]: value }));
    };

    // Return the dropdown options, selected values, and the change handler.
    return { dropdownOptions, selectedValues, handleDropdownChange };
}
